import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useMedia } from 'react-use';
import { Columns } from 'ui-base';
import { Icon, Typography } from 'ck-ui';
import { getImgixUrl } from 'ck-utils';
import { getPriceString, getSalePercentage } from 'ck-utils/price';
import layout from 'ck-utils/layout.module.scss';
import { useWishlist } from 'ck-utils/hooks/useWishlist';
import { BundleLookPageContext } from '../../context';
import { BundleLookStickyHeader } from '../../bundleLookWidgets';
import { ProductHit } from '../ProductHit';
import styles from './CuratedProductList.module.scss';

const CuratedProductList = ({
  listName,
  featureImage,
  hasMultiProductsPerGroup,
  groupsHits,
  groupIcons,
  groupNames,
  indexName
}) => {
  const { bundle, groups, onDeleteProduct, onAddProduct } = useContext(BundleLookPageContext) || {};
  const isMobile = useMedia('(max-width: 767px)');
  const { wishListProductIds, wishListCollections } = useWishlist();

  // Do some Shopify Theme init after product list renders
  useEffect(() => {
    if (hasMultiProductsPerGroup) {
      window.theme.initSlider();
    }
    if (isMobile) {
      window.theme.createProductImgCarousel();
    }
    window.theme.afterpayMarketingModal();
  }, [isMobile, hasMultiProductsPerGroup]);

  const getProgressBarCurrentSelection = (hit, groupPosition) => {
    const { products } = groups[groupPosition];
    return products.reduce((acc, product, productPosition) => {
      const { variantId } = product;
      if (!variantId) return acc;
      return hit.variants.reduce((prev, variant) => (
        variant.id.indexOf(variantId) !== -1
          ? { selectedVariantId: variantId, productPosition }
          : prev
      ), acc);
    }, { selectedVariantId: null, productPosition: 0 });
  };

  const getPageTitle = () => {
    const string = listName.trim().toLowerCase();

    // Bundle Title
    const wordBundle = string.slice(-6);
    if (wordBundle === 'bundle') {
      return [string.replace(wordBundle, ''), wordBundle];
    }

    // Look Title
    const wordLook = string.slice(-4);
    if (wordLook === 'look') {
      return [string.replace(wordLook, ''), wordLook];
    }

    // Best Duo
    const [type, ...rest] = string.split('-');
    if (rest.length > 0) return [rest.join('-'), type];

    // Normal Title
    return [string];
  };

  const pageTitle = getPageTitle();
  const { price, compareAtPrice } = bundle || {};

  return (
    <section className={styles.root}>
      {!isMobile && (
        <header className={styles.hero} style={{ backgroundImage: `url(${featureImage})` }}>
          <div className={classnames(styles.listName, 'container')}>
            <Typography renderAs="h1" variant="heading1" color="white" uppercase>{pageTitle[0]}</Typography>
            {pageTitle[1] && <Typography renderAs="h2" variant="heading3" color="white" uppercase>{pageTitle[1]}</Typography>}
            {bundle && (
              <Typography variant="body-medium" className={layout.marginTop}>
                <span
                  className={classnames('sash-text has-background-danger has-text-white', layout.marginRight)}
                >
                  {`Save up to ${getSalePercentage(price, compareAtPrice)}%`}
                </span>
                <span className="product-price product-price--on-sale">{getPriceString(price)}</span>
                <span className="product-compare-price strike-through has-text-white">{getPriceString(compareAtPrice)}</span>
                <div className="has-afterpay">
                  <a className="js-afterpay-popup has-text-white" href="#">
                    Or <span className="js-afterpay-price pdp-afterpay">{getPriceString(Number(price) / 4)}</span>
                    <span className="pdp-afterpay-pmt has-text-white-bis"> (over 4 payments) </span>
                    <img
                      className="afterpay-logo"
                      alt="afterpay"
                      src="//cdn.shopify.com/s/files/1/1023/3455/t/3/assets/afterpay_logo_white.svg?v=16034147649252765906"
                    />
                  </a>
                </div>
              </Typography>
            )}
          </div>
        </header>
      )}
      <div className="container">
        <Columns className={classnames(styles.columns, 'is-variable', 'is-8')} multiline>
          <BundleLookStickyHeader className={classnames('column', { [layout.marginBottomLarge]: !isMobile })} isBundle={!!bundle} />
          <Columns.Column size={hasMultiProductsPerGroup ? 7 : 8} className={classnames(styles.gallery, 'product-slider-parent')}>
            <div className="product-image-slider white-dot">
              <div className={classnames('product-image-single no-zoom is-relative', styles.imageWrapper)}>
                <img
                  src={getImgixUrl(featureImage, [
                    'fit=crop',
                    'w=800',
                    'h=1000',
                  ])}
                  alt={listName}
                />
                {isMobile && (
                  <div className={styles.listName}>
                    <Typography renderAs="h1" variant="heading3" color="white" uppercase>
                      {listName}
                    </Typography>
                    {bundle && (
                      <div className="product-tile-price-parent">
                        <span
                          className={classnames('sash-text has-background-danger has-text-white', layout.marginRight)}
                        >
                          {`Save up to ${getSalePercentage(price, compareAtPrice)}%`}
                        </span>
                        <span className="product-price product-price--on-sale">{getPriceString(price)}</span>
                        <span className="product-compare-price strike-through has-text-white">{getPriceString(compareAtPrice)}</span>
                        <div className="has-afterpay">
                          <a className="js-afterpay-popup has-text-white" href="#">
                            Or <span className="js-afterpay-price pdp-afterpay">{getPriceString(Number(price) / 4)}</span>
                            <span className="pdp-afterpay-pmt has-text-white-bis"> (over 4 payments) </span>
                            <img
                              className="afterpay-logo"
                              alt="afterpay"
                              src="//cdn.shopify.com/s/files/1/1023/3455/t/3/assets/afterpay_logo_white.svg?v=16034147649252765906"
                            />
                          </a>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {groupsHits.map(group => group.map(hit => (
                <div key={hit.objectID} className={classnames('product-image-single is-relative no-zoom', styles.imageWrapper)}>
                  <img src={getImgixUrl(hit.image)} alt={hit.title} />
                </div>
              )))}
            </div>
          </Columns.Column>
          <Columns.Column size={hasMultiProductsPerGroup ? 5 : 4} className={styles.contentColumn}>
            <div className="is-clipped">
              {groupsHits.map((group, groupPosition) => (
                // eslint-disable-next-line react/no-array-index-key
                <div className={layout.marginBottomLarge} key={groupPosition}>
                  <Typography className={classnames(styles.heading, layout.marginBottom)} variant="heading6" renderAs="h2" uppercase>
                    <span className={styles.headingContent}>
                      <Icon size="large" name={groupIcons[groupPosition]} className={styles.headingIcon} />
                      {!isMobile && 'Select'} {groupNames[groupPosition]} {!!bundle && `(${groups[groupPosition].selectedCount}/${groups[groupPosition].groupCount})`}
                    </span>
                  </Typography>
                  <div className="is-relative curated-product-list">
                    <Columns className={classnames('scrollable is-variable is-3 is-mobile', { 'zoomed-carousel': hasMultiProductsPerGroup })} multiline={false}>
                      {group.map((hit) => {
                        const { selectedVariantId, productPosition } = getProgressBarCurrentSelection(hit, groupPosition);
                        return (
                          <Columns.Column
                            key={hit.objectID}
                            className={classnames({
                              [styles.hitWrapper]: hasMultiProductsPerGroup,
                              [styles.desktopSmaller]: hasMultiProductsPerGroup && !bundle,
                              [styles.hitWrapperSingle]: !hasMultiProductsPerGroup
                            })}
                          >
                            <ProductHit
                              listName={listName}
                              indexName={indexName}
                              isBundle={!!bundle}
                              showSingleImage
                              canClickThrough={false}
                              showQuickView
                              showSash={false}
                              showAddToCartAtBottom
                              hit={hit}
                              addedToWishList={wishListProductIds?.includes(`${hit.id}`)}
                              wishlistHasLoaded={Boolean(wishListProductIds)}
                              wishListCollections={wishListCollections}
                              isMobile={isMobile}
                              selectedVariantId={selectedVariantId}
                              onDeleteProduct={() => {
                                onDeleteProduct(groupPosition, productPosition);
                              }}
                              onAddProduct={(variantId) => onAddProduct(groupPosition, {
                                variantId,
                                imageUrl: hit.image,
                                vendor: hit.vendor,
                                title: hit.title,
                                shortTitle: hit.shortTitle,
                                price: hit.price
                              })}
                            />
                          </Columns.Column>
                        );
                      })}
                    </Columns>
                  </div>
                </div>
              ))}
            </div>
          </Columns.Column>
        </Columns>
      </div>
    </section>
  );
};

CuratedProductList.propTypes = {
  featureImage: PropTypes.string.isRequired,
  groupIcons: PropTypes.array.isRequired,
  groupNames: PropTypes.array.isRequired,
  groupsHits: PropTypes.array.isRequired,
  hasMultiProductsPerGroup: PropTypes.bool.isRequired,
  listName: PropTypes.string.isRequired,
  indexName: PropTypes.string.isRequired
};

export { CuratedProductList };
